import React, { useState, useEffect, useRef } from 'react'
import {
  Award, CheckCircle, XCircle, Clock, RotateCcw,
  ChevronRight, AlertTriangle, Star, Target, Brain,
  PenTool, BookOpen, Lightbulb,
  ChevronDown, ChevronUp, Trophy, Zap, BarChart2, Youtube,
  X, Repeat
} from 'lucide-react'
import { useProgressStore, PASS_SCORE, useUnitProgress } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'N2-1'

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string[]
  concept: string
  misconception?: string
  misconceptionCode?: string
  learnStepId?: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'R01', number: 1, category: '소수 분류', difficulty: 'easy',
    question: '다음 중 유한소수인 것은?',
    choices: ['0.333...', '0.142857...', '0.75', '3.14159...'],
    answer: 2,
    explanation: [
      '유한소수: 소수점 아래 자릿수가 유한한(끝이 있는) 소수',
      '0.333... → 3이 반복 → 순환소수 (무한)',
      '0.142857... → 142857이 반복 → 순환소수 (무한)',
      '0.75 → 소수점 아래 두 자리에서 끝남 → 유한소수 ✓',
      '3.14159... → 반복 없는 무한소수 (π, 무리수)',
    ],
    concept: '유한소수', misconceptionCode: 'NM01', learnStepId: 'NR01',
    misconception: '소수점 아래가 끝나는지 확인해야 합니다!'
  },
  {
    id: 'R02', number: 2, category: '소수 분류', difficulty: 'easy',
    question: '다음 중 순환소수가 아닌 것은?',
    choices: ['0.272727...', '0.123123...', '1/3을 소수로 나타낸 것', 'π (파이)'],
    answer: 3,
    explanation: [
      '순환소수: 소수점 아래에서 일정한 숫자가 반복되는 무한소수',
      '0.272727... → 27 반복 → 순환소수',
      '0.123123... → 123 반복 → 순환소수',
      '1/3 = 0.333... → 3 반복 → 순환소수',
      'π = 3.14159265... → 반복 패턴 없음 → 비순환 무한소수 (무리수) ✓',
    ],
    concept: '순환소수', misconceptionCode: 'NM02', learnStepId: 'NR01',
    misconception: 'π는 순환하지 않는 무한소수(무리수)입니다!'
  },
  {
    id: 'R03', number: 3, category: '순환마디', difficulty: 'easy',
    question: '0.572572572...의 순환마디는?',
    choices: ['5', '57', '572', '5725'],
    answer: 2,
    explanation: [
      '순환마디: 반복되는 숫자의 한 묶음(최소 단위)',
      '0.572572572... = 572가 반복',
      '순환마디 = 572 (3자리) ✓',
    ],
    concept: '순환마디', misconceptionCode: 'NM03', learnStepId: 'NR02',
    misconception: '반복되는 최소 단위가 순환마디입니다!'
  },
  {
    id: 'R04', number: 4, category: '점 표기법', difficulty: 'easy',
    question: '0.13131313...을 점 표기법으로 바르게 나타낸 것은?',
    choices: ['0.13̇', '0.1̇3̇', '0.1̇3', '0.13'],
    answer: 1,
    explanation: [
      '순환마디: 13 (2자리)',
      '점 표기: 순환마디 첫 자리(1) 위와 끝 자리(3) 위에 점',
      '0.1̇3̇ ✓',
    ],
    concept: '점 표기법', misconceptionCode: 'NM03', learnStepId: 'NR02',
    misconception: '순환마디의 첫 자리와 끝 자리 위에 점을 찍습니다!'
  },
  {
    id: 'R05', number: 5, category: '점 표기법', difficulty: 'medium',
    question: '3.1̇4̇가 나타내는 소수는?',
    choices: ['3.14141414...', '3.11444...', '3.14', '3.114114...'],
    answer: 0,
    explanation: [
      '3.1̇4̇에서 순환마디는 14 (첫 점: 1, 끝 점: 4)',
      '소수점 아래에서 14가 반복',
      '3.14141414... ✓',
    ],
    concept: '점 표기법', misconceptionCode: 'NM04', learnStepId: 'NR02',
    misconception: '점이 찍힌 부분이 반복되는 순환마디입니다!'
  },
  {
    id: 'R06', number: 6, category: '분수 변환', difficulty: 'medium',
    question: '0.3̇을 분수로 나타내면?',
    choices: ['3/10', '1/3', '3/99', '1/9'],
    answer: 1,
    explanation: [
      'x = 0.333...으로 놓기',
      '10x = 3.333...',
      '10x - x = 3 → 9x = 3',
      'x = 3/9 = 1/3 ✓',
    ],
    concept: '순환소수→분수', misconceptionCode: 'NM05', learnStepId: 'NR03',
    misconception: '순환소수를 x로 놓고 방정식으로 풀어야 합니다!'
  },
  {
    id: 'R07', number: 7, category: '분수 변환', difficulty: 'medium',
    question: '0.2̇7̇을 분수로 나타내면?',
    choices: ['27/100', '3/11', '27/90', '27/99'],
    answer: 1,
    explanation: [
      'x = 0.272727...으로 놓기',
      '순환마디 27 (2자리) → 100배',
      '100x = 27.2727...',
      '100x - x = 27 → 99x = 27',
      'x = 27/99 = 3/11 ✓ (기약분수)',
    ],
    concept: '순환소수→분수', misconceptionCode: 'NM05', learnStepId: 'NR03',
    misconception: '순환마디 2자리이면 100배를 곱해야 합니다!'
  },
  {
    id: 'R08', number: 8, category: '분수 변환', difficulty: 'hard',
    question: '0.13̇을 분수로 나타내면? (순환마디: 3, 1은 순환 안 함)',
    choices: ['13/99', '2/15', '13/90', '4/30'],
    answer: 1,
    explanation: [
      'x = 0.1333...으로 놓기',
      '10x = 1.333..., 100x = 13.333...',
      '100x - 10x = 12 → 90x = 12',
      'x = 12/90 = 2/15 ✓',
    ],
    concept: '순환소수→분수(혼합)', misconceptionCode: 'NM06', learnStepId: 'NR03',
    misconception: '순환 전 자리가 있으면 10배와 100배를 모두 활용해야 합니다!'
  },
  {
    id: 'R09', number: 9, category: '유한소수 판별', difficulty: 'medium',
    question: '기약분수 3/8이 유한소수인 이유는?',
    choices: [
      '분자 3이 소수이기 때문',
      '분모 8 = 2³이므로 소인수가 2뿐이기 때문',
      '분모가 짝수이기 때문',
      '3과 8이 서로소이기 때문',
    ],
    answer: 1,
    explanation: [
      '유한소수 판별: 기약분수의 분모를 소인수분해',
      '8 = 2³ → 소인수가 2뿐',
      '소인수가 2와 5만이면 유한소수',
      '3/8 = 0.375 (유한소수) ✓',
    ],
    concept: '유한소수 판별', misconceptionCode: 'NM07', learnStepId: 'NR04',
    misconception: '분모의 소인수가 2와 5뿐이면 유한소수가 됩니다!'
  },
  {
    id: 'R10', number: 10, category: '유한소수 판별', difficulty: 'medium',
    question: '다음 중 순환소수가 되는 분수는?',
    choices: ['1/4', '3/20', '5/12', '7/25'],
    answer: 2,
    explanation: [
      '1/4: 4 = 2² → 소인수 2뿐 → 유한소수',
      '3/20: 20 = 2²×5 → 소인수 2, 5뿐 → 유한소수',
      '5/12: 12 = 2²×3 → 소인수에 3 포함 → 순환소수 ✓',
      '7/25: 25 = 5² → 소인수 5뿐 → 유한소수',
    ],
    concept: '유한소수 판별', misconceptionCode: 'NM07', learnStepId: 'NR04',
    misconception: '분모에 2, 5 이외의 소인수가 있으면 순환소수입니다!'
  },
  {
    id: 'R11', number: 11, category: '유한소수 판별', difficulty: 'medium',
    question: '6/15를 기약분수로 나타낸 후 유한소수인지 판별하면?',
    choices: [
      '6/15 그대로 → 분모 15 = 3×5 → 순환소수',
      '기약분수 2/5 → 분모 5 → 유한소수',
      '기약분수 2/5 → 분모 5 → 순환소수',
      '기약분수로 못 나타냄',
    ],
    answer: 1,
    explanation: [
      '반드시 기약분수로 먼저 변환!',
      '6/15 = 2/5 (GCD=3으로 약분)',
      '분모 5 = 5¹ → 소인수 5뿐 → 유한소수',
      '2/5 = 0.4 (유한소수) ✓',
    ],
    concept: '기약분수→유한소수 판별', misconceptionCode: 'NM08', learnStepId: 'NR04',
    misconception: '반드시 기약분수로 만든 후 분모의 소인수를 확인하세요!'
  },
  {
    id: 'R12', number: 12, category: '유리수와 소수', difficulty: 'medium',
    question: '다음 중 옳은 설명은?',
    choices: [
      '모든 유한소수는 분수로 나타낼 수 없다',
      '순환소수는 무한소수가 아니다',
      '모든 유리수는 유한소수 또는 순환소수이다',
      '비순환 무한소수도 분수로 나타낼 수 있다',
    ],
    answer: 2,
    explanation: [
      '유한소수는 분수로 나타낼 수 있음 (예: 0.75 = 3/4)',
      '순환소수는 무한소수의 일종! 반복 패턴이 있는 무한소수',
      '모든 유리수 = 유한소수 또는 순환소수 ✓ (핵심 명제)',
      '비순환 무한소수(π, √2 등)는 무리수 → 분수로 나타낼 수 없음',
    ],
    concept: '유리수와 소수 관계', misconceptionCode: 'NM02', learnStepId: 'NR04',
    misconception: '유리수 ↔ 유한소수 or 순환소수, 이것이 핵심 명제입니다!'
  },
  {
    id: 'R13', number: 13, category: '분수 변환', difficulty: 'hard',
    question: '0.4̇2̇를 분수로 나타내면?',
    choices: ['42/100', '14/33', '42/99', '7/17'],
    answer: 1,
    explanation: [
      'x = 0.424242...로 놓기',
      '순환마디 42 (2자리) → 100배',
      '100x = 42.4242...',
      '100x - x = 42 → 99x = 42',
      'x = 42/99 = 14/33 ✓ (GCD=3으로 기약)',
    ],
    concept: '순환소수→분수', misconceptionCode: 'NM05', learnStepId: 'NR03',
    misconception: '42/99를 기약분수 14/33으로 약분해야 합니다!'
  },
  {
    id: 'R14', number: 14, category: '유한소수 판별', difficulty: 'hard',
    question: 'a/42가 유한소수가 되려면 자연수 a가 만족해야 하는 조건은? (단, a/42는 기약분수가 아닐 수 있음)',
    choices: [
      'a는 짝수여야 한다',
      'a는 42의 배수여야 한다',
      'a는 21의 배수여야 한다',
      'a는 6의 배수여야 한다',
    ],
    answer: 2,
    explanation: [
      '42 = 2 × 3 × 7',
      '유한소수가 되려면 기약분수의 분모에 2, 5 외 소인수가 없어야 함',
      '42의 소인수 중 3과 7을 분자 a로 없애야 함',
      '3 × 7 = 21이 a의 인수여야 함 → a는 21의 배수 ✓',
      '예: a=21이면 21/42 = 1/2 → 유한소수',
    ],
    concept: '유한소수 조건', misconceptionCode: 'NM07', learnStepId: 'NR04',
    misconception: '분모의 2, 5 외 소인수를 분자로 약분해야 합니다!'
  },
  {
    id: 'R15', number: 15, category: '분수 변환', difficulty: 'hard',
    question: '0.31̇4̇를 분수로 나타내면? (순환마디: 14)',
    choices: ['314/999', '311/990', '31/99', '314/990'],
    answer: 1,
    explanation: [
      'x = 0.31414...로 놓기',
      '10x = 3.1414..., 1000x = 314.1414...',
      '1000x - 10x = 311 → 990x = 311',
      'x = 311/990 ✓ (더 이상 약분 안 됨)',
    ],
    concept: '순환소수→분수(혼합)', misconceptionCode: 'NM06', learnStepId: 'NR05',
    misconception: '순환 전 1자리(3)가 있으므로 10x와 1000x를 활용합니다!'
  },
]

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
interface N3TestPageProps { onGoLearn?: (stepId: string) => void }

export default function N3TestPage({ onGoLearn }: N3TestPageProps) {
  const [phase, setPhase] = useState<'intro' | 'test' | 'result'>('intro')
  const [current, setCurrent] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [showWrong, setShowWrong] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [expandedExpl, setExpandedExpl] = useState<Set<number>>(new Set())
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  useEffect(() => {
    if (phase === 'test') {
      intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [phase])

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  const handleSubmit = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer && q.misconceptionCode)
      .map(q => q.misconceptionCode!)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: seconds,
      answers: answers as number[],
      wrongConceptIds: TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('result')
  }

  const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correctCount / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE
  const wrongQuestions = TEST_QUESTIONS.filter((_, i) => answers[i] !== TEST_QUESTIONS[i].answer)
  const answeredCount = answers.filter(a => a !== null).length

  // ── 인트로 ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="card text-center">
          <div className="text-6xl mb-4">🔄</div>
          <h1 className="text-2xl font-black text-slate-800 mb-1">유리수와 순환소수 실전 TEST</h1>
          <p className="text-slate-500 mb-6 text-sm">N2-1 단원 전 범위 · {TEST_QUESTIONS.length}문항 · 25분</p>
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { icon: <PenTool size={18} className="text-blue-500" />, label: '문항 수', value: `${TEST_QUESTIONS.length}문항` },
              { icon: <Clock size={18} className="text-orange-500" />, label: '권장 시간', value: '25분' },
              { icon: <Trophy size={18} className="text-yellow-500" />, label: '합격 기준', value: `${PASS_SCORE}점` },
            ].map(item => (
              <div key={item.label} className="bg-slate-50 rounded-xl p-3">
                <div className="flex justify-center mb-1">{item.icon}</div>
                <p className="text-xs text-slate-500">{item.label}</p>
                <p className="text-sm font-black text-slate-800">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="text-left bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-xs font-bold text-blue-700 mb-2">📋 출제 범위</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-slate-600">
              {['NR01 유한·무한·순환소수', 'NR02 순환마디·점 표기법', 'NR03 순환소수→분수 변환', 'NR04 유한소수 판별', 'NR05 혼합 변환 심화'].map(t => (
                <div key={t} className="flex items-center gap-1"><span className="text-blue-400">•</span> {t}</div>
              ))}
            </div>
          </div>
          {unitProgress.testRecords.length > 0 && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-left">
              <p className="text-xs font-bold text-amber-700 mb-1">📊 이전 응시 기록</p>
              <p className="text-sm font-black text-amber-800">
                최고 점수: {unitProgress.bestScore}점 {unitProgress.passed && '🏆 합격'}
              </p>
              <p className="text-xs text-amber-600">총 {unitProgress.testRecords.length}회 응시</p>
            </div>
          )}
          <button
            onClick={() => { setPhase('test'); setCurrent(0); setSeconds(0); setAnswers(Array(TEST_QUESTIONS.length).fill(null)) }}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-black text-lg shadow-lg hover:shadow-xl transition-all"
          >
            시험 시작 →
          </button>
        </div>
      </div>
    )
  }

  // ── 시험 ────────────────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = TEST_QUESTIONS[current]
    const answered = answers[current]

    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">{current + 1} / {TEST_QUESTIONS.length}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${q.difficulty === 'easy' ? 'bg-green-100 text-green-700' : q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
              {q.difficulty === 'easy' ? '쉬움' : q.difficulty === 'medium' ? '보통' : '어려움'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-mono font-bold text-sm bg-slate-100 text-slate-700">
            <Clock size={14} /> {fmt(seconds)}
          </div>
        </div>
        <div className="bg-slate-100 rounded-full h-1.5 mb-5 overflow-hidden">
          <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
        </div>
        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-slate-400">{q.category}</span>
          </div>
          <p className="text-base font-bold text-slate-800 leading-relaxed mb-5">{q.number}. {q.question}</p>
          <div className="space-y-2.5">
            {q.choices.map((choice, i) => (
              <button key={i}
                onClick={() => { const next = [...answers]; next[current] = i; setAnswers(next) }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${answered === i ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-sm' : 'border-slate-200 bg-white hover:border-blue-300 text-slate-700'}`}
              >
                <span className="font-black mr-2 text-slate-400">{['①','②','③','④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button disabled={current === 0} onClick={() => setCurrent(c => c - 1)}
            className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-blue-500 text-white font-bold text-sm hover:bg-blue-600">
              다음 →
            </button>
          ) : (
            <button onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-sm hover:opacity-90">
              제출하기 ✓
            </button>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${i === current ? 'bg-blue-500 text-white' : answers[i] !== null ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
              {i + 1}
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-2">
          답변 완료: {answeredCount}/{TEST_QUESTIONS.length}
        </p>
      </div>
    )
  }

  // ── 결과 ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      <div className={`card text-center border-2 ${passed ? 'border-emerald-300 bg-emerald-50' : 'border-orange-300 bg-orange-50'}`}>
        <div className="text-5xl mb-3">{passed ? '🏆' : '📚'}</div>
        <h2 className="text-3xl font-black mb-1" style={{ color: passed ? '#059669' : '#ea580c' }}>{score}점</h2>
        <p className={`text-base font-bold mb-4 ${passed ? 'text-emerald-700' : 'text-orange-700'}`}>
          {passed ? '🎉 합격! 유리수·순환소수 완전 마스터!' : `아직 ${PASS_SCORE - score}점 부족해요. 다시 도전!`}
        </p>
        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto mb-5">
          {[
            { label: '정답', value: `${correctCount}문항`, color: 'text-emerald-600' },
            { label: '오답', value: `${TEST_QUESTIONS.length - correctCount}문항`, color: 'text-red-500' },
            { label: '소요 시간', value: fmt(seconds), color: 'text-slate-600' },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-xl p-2">
              <p className={`text-lg font-black ${item.color}`}>{item.value}</p>
              <p className="text-xs text-slate-500">{item.label}</p>
            </div>
          ))}
        </div>
        <button
          onClick={() => { setPhase('intro'); setAnswers(Array(TEST_QUESTIONS.length).fill(null)); setSeconds(0); setShowWrong(false); setExpandedExpl(new Set()) }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"
        >
          <RotateCcw size={14} /> 다시 풀기
        </button>
      </div>

      {/* AI 취약점 분석 */}
      {predictedFragments.length > 0 && (
        <div className="card border-2 border-violet-200 bg-violet-50">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={16} className="text-violet-600" />
            <span className="text-sm font-black text-violet-800">🤖 AI 취약점 분석</span>
          </div>
          <div className="space-y-2">
            {predictedFragments.map(pred => {
              const tagConf = FRAGMENT_TAG_CONFIG[pred.tag]
              return (
                <div key={pred.fragment.id} className={`rounded-xl border ${tagConf.color} p-3`}>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{pred.fragment.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tagConf.badge}`}>{tagConf.label}</span>
                        <span className="text-xs font-mono text-slate-500">{pred.fragment.misconceptionCode}</span>
                      </div>
                      <p className="text-xs font-bold text-slate-800">{pred.fragment.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{pred.fragment.fixTip}</p>
                      {onGoLearn && (
                        <button onClick={() => onGoLearn(pred.fragment.stepId)}
                          className="mt-2 text-xs px-2.5 py-1 rounded-lg bg-violet-600 text-white font-bold hover:bg-violet-700">
                          → {pred.fragment.stepTitle} 학습
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* 오답 노트 */}
      {wrongQuestions.length > 0 && (
        <div className="card">
          <button onClick={() => setShowWrong(r => !r)} className="w-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              <span className="text-sm font-black text-slate-800">오답 노트 ({wrongQuestions.length}문제)</span>
            </div>
            {showWrong ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showWrong && (
            <div className="mt-4 space-y-4">
              {wrongQuestions.map(q => {
                const qIdx = TEST_QUESTIONS.indexOf(q)
                const myAns = answers[qIdx]
                const isExpanded = expandedExpl.has(qIdx)
                const video = getApprovedByTag(q.learnStepId ?? '')
                return (
                  <div key={q.id} className="border-2 border-red-100 rounded-xl overflow-hidden">
                    <div className="bg-red-50 px-4 py-3">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-black text-red-600">문제 {q.number}</span>
                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{q.category}</span>
                        {q.misconceptionCode && <span className="text-xs font-mono bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">{q.misconceptionCode}</span>}
                      </div>
                      <p className="text-sm font-semibold text-slate-800">{q.question}</p>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      <div className="grid grid-cols-2 gap-1.5">
                        {q.choices.map((c, i) => (
                          <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${i === q.answer ? 'bg-emerald-100 text-emerald-800 font-bold border border-emerald-300' : i === myAns ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-slate-50 text-slate-500'}`}>
                            {i === q.answer && <CheckCircle size={10} className="text-emerald-600 flex-shrink-0" />}
                            {i === myAns && i !== q.answer && <XCircle size={10} className="text-red-500 flex-shrink-0" />}
                            {['①','②','③','④'][i]} {c}
                          </div>
                        ))}
                      </div>
                      {q.misconception && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <p className="text-xs font-bold text-amber-700">⚠️ 오개념 경고</p>
                          <p className="text-xs text-amber-800 mt-0.5">{q.misconception}</p>
                        </div>
                      )}
                      <button
                        onClick={() => setExpandedExpl(s => { const n = new Set(s); n.has(qIdx) ? n.delete(qIdx) : n.add(qIdx); return n })}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-semibold"
                      >
                        <Lightbulb size={11} />{isExpanded ? '해설 닫기' : '해설 보기'}
                      </button>
                      {isExpanded && (
                        <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                          {q.explanation.map((line, i) => <p key={i} className="text-xs text-slate-700 leading-relaxed">{line}</p>)}
                        </div>
                      )}
                      <div className="flex gap-2 flex-wrap">
                        {onGoLearn && q.learnStepId && (
                          <button onClick={() => onGoLearn(q.learnStepId!)}
                            className="text-xs px-3 py-1.5 rounded-lg bg-blue-500 text-white font-bold hover:bg-blue-600 flex items-center gap-1">
                            <BookOpen size={11} /> 개념 다시 보기 ({q.learnStepId})
                          </button>
                        )}
                        {video && (
                          <button onClick={() => setVideoModal({ stepId: q.learnStepId!, label: q.concept })}
                            className="text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 flex items-center gap-1">
                            <Youtube size={11} /> 영상
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* 전체 답안 */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <BarChart2 size={16} className="text-slate-500" />
          <span className="text-sm font-bold text-slate-700">전체 답안 확인</span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {TEST_QUESTIONS.map((q, i) => {
            const correct = answers[i] === q.answer
            return (
              <div key={i} className={`rounded-lg p-2 text-center ${correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <p className="text-xs font-bold text-slate-500">{i + 1}번</p>
                {correct ? <CheckCircle size={14} className="text-emerald-500 mx-auto" /> : <XCircle size={14} className="text-red-500 mx-auto" />}
              </div>
            )
          })}
        </div>
      </div>

      {videoModal && (
        <ConceptVideoModal conceptTag={videoModal.stepId} conceptLabel={videoModal.label} viewerId="student_001" onClose={() => setVideoModal(null)} onCompleted={() => setVideoModal(null)} />
      )}
    </div>
  )
}
